/**
 * Media-query hooks for the responsive shell.
 *
 * Breakpoints mirror Tailwind v4's defaults so JS and CSS agree:
 *   sm 640 · md 768 · lg 1024 · xl 1280
 *
 * `useIsMobile()` is true below `md` (phones, small tablets in portrait):
 * the width where tables become cards and dialogs become sheets.
 * `useIsDesktopNav()` is true at `lg` and up: the width where the sidebar
 * is docked rather than an off-canvas drawer.
 *
 * Built on useSyncExternalStore so there is no mount flash and no
 * setState-in-effect. SSR/jsdom without matchMedia → false.
 */

import { useCallback, useSyncExternalStore } from 'react'

export const BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280 } as const
export type Breakpoint = keyof typeof BREAKPOINTS

function getMql(query: string): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  return window.matchMedia(query)
}

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = getMql(query)
      if (!mql) return () => {}
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', onChange)
        return () => mql.removeEventListener('change', onChange)
      }
      // Safari < 14
      mql.addListener?.(onChange)
      return () => mql.removeListener?.(onChange)
    },
    [query],
  )
  const getSnapshot = useCallback(() => getMql(query)?.matches ?? false, [query])
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/** True when the viewport is narrower than the given breakpoint. */
export function useBelow(bp: Breakpoint): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS[bp] - 0.02}px)`)
}

/** Phones / small tablets: below `md` (768 px). */
export function useIsMobile(): boolean {
  return useBelow('md')
}

/** Docked-sidebar layout: `lg` (1024 px) and up. */
export function useIsDesktopNav(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`)
}
